"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { SPORTSBOOKS } from "@/lib/sportsbook-registry";
import { cn } from "@/lib/utils";
import {
  type BookWeights,
  CONSENSUS_BOOKS,
  WEIGHT_PRESETS,
  OFFICIAL_PRESETS,
  getBookCategory,
} from "@/lib/weights-config";
import type { UserWeightPreset } from "@/lib/actions/weight-presets";
import { comparisonColumnLabel, WHALES_COLUMN, WHALES_LOCKED_LABEL } from "@/lib/whales";
import { DIAMOND_PILL_CLASS } from "@/components/billing/diamond-tags";
import { Star, Trash2, Plus, Loader2, Lock, LockOpen } from "lucide-react";

export interface WeightsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: BookWeights | null;
  activePresetName?: string | null;
  guidedPresetKey?: string;
  requiredBooks?: string[];
  onApply: (weights: BookWeights | null, presetName: string | null, requiredBooks: string[]) => void;
  mobile?: boolean;
  userPresets?: UserWeightPreset[];
  onSavePreset?: (name: string, weights: BookWeights, requiredBooks: string[]) => Promise<void>;
  onDeletePreset?: (name: string) => Promise<void>;
  onSetDefault?: (name: string | null) => Promise<void>;
  maxPresets?: number;
  availableBooks?: readonly string[];
  whales: WhalesRow;
}

export type WhalesRow =
  | { readonly kind: "editable" }
  | { readonly kind: "locked"; readonly unlock: () => void; readonly upsell: ReactNode }
  | { readonly kind: "whenHeld" }
  | { readonly kind: "hidden" };

const MAX_PRESETS_DEFAULT = 5;

function lockedWhalesNote(weights: BookWeights | null, weighted: boolean, required: boolean): string {
  if (weighted) {
    const only = weights !== null && Object.keys(weights).length === 1;
    return `Your Whales weight is saved. It counts toward fair value again when you're on Diamond.${only ? " Until then, fair value uses every book." : ""}`;
  }
  if (required) return "Your Whales requirement is saved. It applies again when you're on Diamond.";
  return "Add the whale money waiting on every exchange to your fair value.";
}

export function WeightsDialog({
  open,
  onOpenChange,
  value,
  activePresetName,
  guidedPresetKey,
  requiredBooks = [],
  onApply,
  mobile = false,
  userPresets = [],
  onSavePreset,
  onDeletePreset,
  onSetDefault,
  maxPresets = MAX_PRESETS_DEFAULT,
  availableBooks,
  whales,
}: WeightsDialogProps) {
  const [draftWeights, setDraftWeights] = useState<BookWeights | null>(value);
  const [draftPresetName, setDraftPresetName] = useState<string | null>(activePresetName ?? null);
  const [draftRequiredBooks, setDraftRequiredBooks] = useState<Set<string>>(new Set(requiredBooks));
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isSettingDefault, setIsSettingDefault] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const sessionBooksRef = useRef(new Set<string>());
  const wasOpenRef = useRef(false);

  const resolvedBooks = useMemo(() => {
    const bookSet = new Set<string>(availableBooks ?? Object.keys(SPORTSBOOKS));
    if (value) {
      for (const k of Object.keys(value)) bookSet.add(k);
    }
    for (const b of requiredBooks) bookSet.add(b);
    if (draftWeights) {
      for (const k of Object.keys(draftWeights)) bookSet.add(k);
    }
    for (const b of draftRequiredBooks) bookSet.add(b);
    for (const b of sessionBooksRef.current) bookSet.add(b);
    for (const b of bookSet) sessionBooksRef.current.add(b);
    bookSet.delete(WHALES_COLUMN);
    return Array.from(bookSet);
  }, [availableBooks, value, requiredBooks, draftWeights, draftRequiredBooks]);

  const { sharpBooks, retailBooks, otherBooks } = useMemo(() => {
    const consensusOrder = new Map(CONSENSUS_BOOKS.map((b, i) => [b.id, i]));
    const sharp: string[] = [];
    const retail: string[] = [];
    const other: string[] = [];

    for (const id of resolvedBooks) {
      const cat = getBookCategory(id);
      if (cat === "sharp") sharp.push(id);
      else if (cat === "retail") retail.push(id);
      else other.push(id);
    }

    const byConsensus = (a: string, b: string) =>
      (consensusOrder.get(a) ?? 999) - (consensusOrder.get(b) ?? 999);
    sharp.sort(byConsensus);
    retail.sort(byConsensus);
    other.sort((a, b) => comparisonColumnLabel(a).localeCompare(comparisonColumnLabel(b)));

    return { sharpBooks: sharp, retailBooks: retail, otherBooks: other };
  }, [resolvedBooks]);

  const allBooksOrdered = useMemo(
    () => [...sharpBooks, ...retailBooks, ...otherBooks],
    [sharpBooks, retailBooks, otherBooks],
  );

  const defaultWeights = useMemo<BookWeights>(() => Object.fromEntries(allBooksOrdered.map((id) => [id, 1])), [allBooksOrdered]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      sessionBooksRef.current = new Set<string>();
      setDraftWeights(value);
      setDraftPresetName(activePresetName ?? null);
      setDraftRequiredBooks(new Set(requiredBooks));
      setShowSaveDialog(false);
      setNewPresetName("");
      setSaveError(null);
    }
    wasOpenRef.current = open;
  }, [open, value, activePresetName, requiredBooks]);

  const isBookEnabled = (bookId: string) => (draftWeights === null ? allBooksOrdered.includes(bookId) : bookId in draftWeights);

  const whalesWeighted = draftWeights !== null && WHALES_COLUMN in draftWeights;
  const whalesRequired = draftRequiredBooks.has(WHALES_COLUMN);
  const whalesShown = whales.kind === "editable" || (whales.kind === "whenHeld" && (whalesWeighted || whalesRequired));

  const getBookWeight = (bookId: string) => {
    if (draftWeights === null) return 1;
    return draftWeights[bookId] ?? 1;
  };

  const toggleBook = (bookId: string) => {
    setDraftPresetName(null);

    const current = draftWeights ?? defaultWeights;
    if (bookId in current) {
      const next = { ...current };
      delete next[bookId];
      setDraftWeights(draftWeights !== null && Object.keys(next).length === 0 ? null : next);
      setDraftRequiredBooks(prev => {
        const next = new Set(prev);
        next.delete(bookId);
        return next;
      });
    } else {
      setDraftWeights({ ...current, [bookId]: 1 });
    }
  };

  const setBookWeight = (bookId: string, weight: number) => {
    if (!Number.isFinite(weight)) return;
    setDraftPresetName(null);

    setDraftWeights({ ...(draftWeights ?? defaultWeights), [bookId]: Math.max(0.1, weight) });
  };

  const isBookRequired = (bookId: string) => {
    return draftRequiredBooks.has(bookId);
  };

  const toggleBookRequired = (bookId: string) => {
    setDraftPresetName(null);

    setDraftRequiredBooks(prev => {
      const next = new Set(prev);
      if (next.has(bookId)) {
        next.delete(bookId);
      } else {
        next.add(bookId);
      }
      return next;
    });
  };

  const applySystemPreset = (preset: typeof WEIGHT_PRESETS[0]) => {
    setDraftWeights(preset.weights);
    setDraftPresetName(null);
    setDraftRequiredBooks(new Set());
  };

  const applyOfficialPreset = (preset: typeof OFFICIAL_PRESETS[0]) => {
    setDraftWeights(preset.weights);
    setDraftPresetName(preset.label);
    setDraftRequiredBooks(new Set(preset.requiredBooks));
  };

  const applyUserPreset = (preset: UserWeightPreset) => {
    setDraftWeights(preset.weights);
    setDraftPresetName(preset.name);
    setDraftRequiredBooks(new Set(preset.requiredBooks ?? []));
  };

  const activeSystemPreset = WEIGHT_PRESETS.find(p => {
    if (draftRequiredBooks.size > 0) return false;
    if (p.weights === null && draftWeights === null) return true;
    if (p.weights === null || draftWeights === null) return false;
    const pKeys = Object.keys(p.weights).sort();
    const dKeys = Object.keys(draftWeights).sort();
    if (pKeys.length !== dKeys.length) return false;
    return pKeys.every((k, i) => k === dKeys[i] && p.weights![k] === draftWeights[k]);
  });

  const activeOfficialPreset = OFFICIAL_PRESETS.find(p => {
    if (draftWeights === null) return false;
    const pKeys = Object.keys(p.weights).sort();
    const dKeys = Object.keys(draftWeights).sort();
    if (pKeys.length !== dKeys.length) return false;
    const weightsMatch = pKeys.every((k, i) => k === dKeys[i] && p.weights[k] === draftWeights[k]);
    if (!weightsMatch) return false;
    const pReq = [...p.requiredBooks].sort();
    const dReq = [...draftRequiredBooks].sort();
    return pReq.length === dReq.length && pReq.every((r, i) => r === dReq[i]);
  });

  const activeUserPreset = userPresets.find(p => {
    if (draftWeights === null) return false;
    const pKeys = Object.keys(p.weights).sort();
    const dKeys = Object.keys(draftWeights).sort();
    if (pKeys.length !== dKeys.length) return false;
    const wMatch = pKeys.every((k, i) => k === dKeys[i] && p.weights[k] === draftWeights[k]);
    if (!wMatch) return false;
    const pReq = [...(p.requiredBooks ?? [])].sort();
    const dReq = [...draftRequiredBooks].sort();
    return pReq.length === dReq.length && pReq.every((r, i) => r === dReq[i]);
  });

  const handleApply = () => {
    const presetName = activeUserPreset?.name ?? draftPresetName;
    onApply(draftWeights, presetName, Array.from(draftRequiredBooks));
    onOpenChange(false);
  };

  const handleSaveNewPreset = async () => {
    if (!onSavePreset || !draftWeights) return;

    const trimmedName = newPresetName.trim();
    if (!trimmedName) {
      setSaveError("Please enter a name");
      return;
    }
    if (trimmedName.length > 50) {
      setSaveError("Name too long (max 50 characters)");
      return;
    }
    const replacementName = userPresets.find(
      (preset) => preset.name.toLowerCase() === trimmedName.toLowerCase(),
    )?.name;
    if (!replacementName && userPresets.length >= maxPresets) {
      setSaveError(`Maximum ${maxPresets} presets allowed`);
      return;
    }

    const savedName = replacementName ?? trimmedName;

    setIsSaving(true);
    setSaveError(null);
    try {
      await onSavePreset(savedName, draftWeights, Array.from(draftRequiredBooks));
      setDraftPresetName(savedName);
      setShowSaveDialog(false);
      setNewPresetName("");
    } catch {
      setSaveError("Failed to save preset");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePreset = async (name: string) => {
    if (!onDeletePreset) return;

    setIsDeleting(name);
    try {
      await onDeletePreset(name);
      if (draftPresetName === name) {
        setDraftPresetName(null);
      }
    } finally {
      setIsDeleting(null);
    }
  };

  const handleSetDefault = async (name: string, currentlyDefault: boolean) => {
    if (!onSetDefault) return;

    setIsSettingDefault(name);
    try {
      await onSetDefault(currentlyDefault ? null : name);
    } finally {
      setIsSettingDefault(null);
    }
  };

  const atPresetCap = userPresets.length >= maxPresets;
  const canSaveNewPreset = Boolean(onSavePreset) && draftWeights !== null;

  const renderBookList = (bookIds: string[], title: string) => {
    if (bookIds.length === 0) return null;
    return (
      <div role="group" aria-label={title}>
        <h4 className="mb-2 text-sm font-medium text-muted-foreground">{title}</h4>
        <div className="space-y-2">
          {bookIds.map(bookId => {
            const name = comparisonColumnLabel(bookId);
            const enabled = isBookEnabled(bookId);
            const required = isBookRequired(bookId);
            return (
              <div
                key={bookId}
                className={cn(
                  "flex items-center justify-between rounded-md border px-3 py-2 transition-colors",
                  required
                    ? "border-amber-500/50 bg-amber-500/10"
                    : "border-border bg-background/50"
                )}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`weight-${bookId}`}
                    checked={enabled}
                    onCheckedChange={() => toggleBook(bookId)}
                  />
                  <label htmlFor={`weight-${bookId}`} className="cursor-pointer text-sm font-medium">
                    {name}
                  </label>
                  {required && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-500">
                      Required
                    </span>
                  )}
                </div>
                {(enabled || required) && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleBookRequired(bookId)}
                      title={required ? "Remove requirement" : `Require ${name}`}
                      className={cn(
                        "rounded p-1.5 transition-colors",
                        required
                          ? "text-amber-500 hover:text-amber-400 hover:bg-amber-500/20"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      )}
                    >
                      {required ? (
                        <Lock className="size-4" />
                      ) : (
                        <LockOpen className="size-4" />
                      )}
                    </button>
                    {enabled && <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">Wt:</span>
                      <input aria-label={`${name} weight`}
                        type="number"
                        min="0.1"
                        max="10"
                        step="0.1"
                        value={getBookWeight(bookId)}
                        onChange={e => {
                          const numeric = Number(e.target.value);
                          if (!Number.isFinite(numeric)) return;
                          setBookWeight(bookId, numeric);
                        }}
                        className="w-14 rounded border bg-background px-2 py-1 text-right text-sm"
                      />
                    </div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        contentClassName="max-h-[85svh] md:max-h-[85dvh] overflow-hidden flex flex-col"
      >
        <DialogHeader className="shrink-0 pr-6">
          <DialogTitle>Fair Value Settings</DialogTitle>
          <DialogDescription>
            Configure which sportsbooks contribute to the fair odds consensus.
          </DialogDescription>
        </DialogHeader>

        <div className={cn(
          "flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-4 pr-1",
          mobile && "[-webkit-overflow-scrolling:touch] [touch-action:pan-y]"
        )}>
          <div className="space-y-2">
            <span className="text-sm text-muted-foreground">Presets</span>
            <div className="flex flex-wrap gap-2">
              {WEIGHT_PRESETS.map(preset => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => applySystemPreset(preset)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs transition-colors",
                    mobile && "py-1.5 min-h-[36px]",
                    activeSystemPreset?.key === preset.key && !activeUserPreset && !activeOfficialPreset
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-muted-foreground/40 text-muted-foreground hover:border-muted-foreground"
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {OFFICIAL_PRESETS.length > 0 && (
            <div className="space-y-2">
              {guidedPresetKey && <p data-spotlight-context className="rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-xs leading-relaxed text-primary">{activeOfficialPreset?.key === guidedPresetKey ? "Matt’s exact weights are selected. Click the glowing Apply button below, then Apply extras to update the board." : "Click the glowing Matt’s Game Line Weights button. These are Matt’s exact game-line weights; matching his filters and current prices gives you the same +EV comparisons."}</p>}
              <span className="text-sm text-muted-foreground">Recommended</span>
              <div className="flex flex-wrap gap-2">
                {OFFICIAL_PRESETS.map(preset => {
                  const isActive = activeOfficialPreset?.key === preset.key && !activeUserPreset;
                  return (
                    <button
                      key={preset.key}
                      data-guided-preset={guidedPresetKey === preset.key ? "true" : undefined}
                      data-spotlight-target={guidedPresetKey === preset.key && !isActive ? "Click Matt’s preset" : undefined}
                      type="button"
                      onClick={() => applyOfficialPreset(preset)}
                      className={cn(
                        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                        mobile && "py-1.5 min-h-[36px]",
                        guidedPresetKey === preset.key && !isActive && "ring-2 ring-primary ring-offset-2 ring-offset-zinc-950 shadow-[0_0_24px_5px_rgba(88,209,246,0.55)]",
                        isActive
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-primary/30 bg-primary/5 text-primary/70 hover:border-primary/60 hover:text-primary"
                      )}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(userPresets.length > 0 || onSavePreset) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">Your Presets</span>
                {canSaveNewPreset && !showSaveDialog && (
                  <button
                    type="button"
                    onClick={() => setShowSaveDialog(true)}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                  >
                    <Plus className="size-3" />
                    Save Current
                  </button>
                )}
              </div>
              {atPresetCap && (
                <p className="text-xs text-muted-foreground">
                  {userPresets.length}/{maxPresets} presets. Reuse a name to replace, or delete one.
                </p>
              )}

              {showSaveDialog && (
                <div className="flex flex-col gap-2 rounded-md border bg-muted/30 p-3">
                  <input aria-label="Preset name"
                    type="text"
                    placeholder="Preset name…"
                    value={newPresetName}
                    onChange={(e) => {
                      setNewPresetName(e.target.value);
                      setSaveError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveNewPreset();
                      if (e.key === "Escape") setShowSaveDialog(false);
                    }}
                    className="rounded border bg-background px-3 py-1.5 text-sm"
                    maxLength={50}
                  />
                  {saveError && (
                    <span className="text-xs text-destructive">{saveError}</span>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowSaveDialog(false)}
                      className="flex-1 rounded border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveNewPreset}
                      disabled={isSaving || !newPresetName.trim()}
                      className="flex-1 rounded bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {isSaving ? (
                        <Loader2 className="mx-auto size-3 animate-spin" />
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {userPresets.length > 0 && (
                <div className="space-y-1">
                  {userPresets.map(preset => {
                    const isActive = activeUserPreset?.name === preset.name;
                    const isCurrentDefault = preset.isDefault;

                    return (
                      <div
                        key={preset.name}
                        className={cn(
                          "flex items-center justify-between rounded-md border px-3 py-2 transition-colors",
                          isActive ? "border-primary bg-primary/5" : "border-muted hover:border-muted-foreground/50"
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => applyUserPreset(preset)}
                          className="flex-1 text-left text-sm font-medium"
                        >
                          {preset.name}
                          {isCurrentDefault && (
                            <span className="ml-2 text-xs text-muted-foreground">(default)</span>
                          )}
                        </button>
                        <div className="flex items-center gap-1">
                          {onSetDefault && (
                            <button
                              type="button"
                              onClick={() => handleSetDefault(preset.name, isCurrentDefault)}
                              disabled={isSettingDefault === preset.name}
                              className={cn(
                                "rounded p-1.5 transition-colors",
                                isCurrentDefault
                                  ? "text-yellow-500 hover:text-yellow-600"
                                  : "text-muted-foreground hover:text-foreground"
                              )}
                              title={isCurrentDefault ? "Remove as default" : "Set as default"}
                            >
                              {isSettingDefault === preset.name ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : (
                                <Star className={cn("size-4", isCurrentDefault && "fill-current")} />
                              )}
                            </button>
                          )}
                          {onDeletePreset && (
                            <button
                              type="button"
                              onClick={() => handleDeletePreset(preset.name)}
                              disabled={isDeleting === preset.name}
                              className="rounded p-1.5 text-muted-foreground transition-colors hover:text-destructive"
                              title="Delete preset"
                            >
                              {isDeleting === preset.name ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : (
                                <Trash2 className="size-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {userPresets.length === 0 && !showSaveDialog && (
                <p className="text-xs text-muted-foreground">
                  No saved presets. Configure weights below and click &quot;Save Current&quot; to create one.
                </p>
              )}
            </div>
          )}

          <div className="space-y-4 pr-2">
            {renderBookList(sharpBooks, "Sharp Books")}
            {renderBookList(retailBooks, "Retail Books")}
            {renderBookList(otherBooks, "Other Books")}
            {whalesShown && renderBookList([WHALES_COLUMN], "Exchange orders")}
            {whales.kind === "locked" && (
              <div role="group" aria-label="Exchange orders">
                <h4 className="mb-2 text-sm font-medium text-muted-foreground">Exchange orders</h4>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-label={WHALES_LOCKED_LABEL}
                  onClick={whales.unlock}
                  className="flex w-full items-center justify-between rounded-md border border-border bg-background/50 px-3 py-2 text-left transition-colors hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <span className="flex items-center gap-3">
                    <Lock className="size-4 text-zinc-500" aria-hidden="true" />
                    <span className="text-sm font-medium">Whales</span>
                  </span>
                  <span className={DIAMOND_PILL_CLASS}>Diamond</span>
                </button>
                <p className="mt-1.5 text-xs text-zinc-500">{lockedWhalesNote(draftWeights, whalesWeighted, whalesRequired)}</p>
                {whales.upsell}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className={cn(
          "shrink-0 gap-2 border-t pt-4 sm:gap-0",
          mobile && "pb-[env(safe-area-inset-bottom)]"
        )}>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className={cn(
              "rounded-md border px-4 py-2 text-sm text-muted-foreground hover:bg-muted",
              mobile && "min-h-[44px]"
            )}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            data-spotlight-target={guidedPresetKey && activeOfficialPreset?.key === guidedPresetKey ? "Click Apply" : undefined}
            className={cn(
              "rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90",
              mobile && "min-h-[44px]"
            )}
          >
            Apply
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
